// Como publicar: Agora, Agendar (data e hora) ou Recorrente (regra semanal ou
// intervalo). O envio em si fica com a pagina; aqui so a escolha e os campos.

import { CalendarClock, Clock, Loader2, Repeat, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { todayInBrasilia, type RecurrenceDraft } from "@/lib/recurrence-draft";
import { RecurrenceEditor } from "./RecurrenceEditor";

export type PublishMode = "now" | "schedule" | "recurring";

interface PublishModeCardProps {
  readonly mode: PublishMode;
  readonly onModeChange: (mode: PublishMode) => void;
  readonly scheduleDate: string;
  readonly scheduleTime: string;
  readonly onScheduleChange: (date: string, time: string) => void;
  readonly draft: RecurrenceDraft;
  readonly onDraftChange: (draft: RecurrenceDraft) => void;
  readonly canSubmit: boolean;
  readonly isPending: boolean;
  readonly onSubmit: () => void;
}

const MODES: Array<{ mode: PublishMode; label: string; icon: JSX.Element }> = [
  { mode: "now", label: "Agora", icon: <Send size={14} /> },
  { mode: "schedule", label: "Agendar", icon: <CalendarClock size={14} /> },
  { mode: "recurring", label: "Recorrente", icon: <Repeat size={14} /> },
];

const SUBMIT_LABEL: Record<PublishMode, string> = {
  now: "Publicar agora",
  schedule: "Confirmar agendamento",
  recurring: "Salvar publicação recorrente",
};

const HINT: Record<PublishMode, string> = {
  now: "O envio sai em até 30 segundos. Acompanhe cada destino no Calendário.",
  schedule: "O envio sai em até 30 segundos após o horário escolhido (horário de Brasília).",
  recurring:
    "Cada ocorrência entra na fila no horário da regra, respeita a janela de envio e usa a marca d'água e a imagem no WhatsApp. Gerencie em Publicações recorrentes.",
};

export function PublishModeCard(props: PublishModeCardProps): JSX.Element {
  const { mode, onModeChange, canSubmit, isPending, onSubmit } = props;
  return (
    <Card variant="raised">
      <CardContent className="space-y-4 pt-5">
        <div role="radiogroup" aria-label="Quando publicar" className="grid grid-cols-3 gap-2">
          {MODES.map((m) => (
            <button
              key={m.mode}
              type="button"
              role="radio"
              aria-checked={mode === m.mode}
              onClick={() => onModeChange(m.mode)}
              className={cn(
                "inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                mode === m.mode ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {m.icon}
              {m.label}
            </button>
          ))}
        </div>

        {mode === "schedule" && (
          <div className="flex flex-wrap gap-3 rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.35)] p-4">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="schedule-date" variant="field">Data</Label>
              <Input id="schedule-date" type="date" value={props.scheduleDate} min={todayInBrasilia()} onChange={(e) => props.onScheduleChange(e.target.value, props.scheduleTime)} />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="schedule-time" variant="field">Horário</Label>
              <Input id="schedule-time" type="time" value={props.scheduleTime} onChange={(e) => props.onScheduleChange(props.scheduleDate, e.target.value)} />
            </div>
          </div>
        )}

        {mode === "recurring" && <RecurrenceEditor draft={props.draft} onChange={props.onDraftChange} />}

        <Button type="button" variant={mode === "now" ? "default" : "gold"} disabled={!canSubmit} onClick={onSubmit}>
          {isPending ? <Loader2 size={15} className="animate-spin" /> : <Clock size={15} />}
          {SUBMIT_LABEL[mode]}
        </Button>
        <p className="text-xs text-muted-foreground">{HINT[mode]}</p>
      </CardContent>
    </Card>
  );
}
